import { Cloud, Ruler, Scissors, Shirt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { type CraftStep as CraftStepData, LANDING_CONTENT } from "../content";
import { useReveal } from "../useReveal";
import { PhotoSlot } from "./PhotoSlot";
import { REVEAL_TRANSITION, Reveal, revealState } from "./Reveal";

// The deep warm "anchor" section (ADR-0055): a dark atelier band that breaks the
// pale rhythm and gives the page focal depth, sells the craft, and answers the
// "cheap AI / dropship" doubt. The three steps are the pillow's real making beats
// (outline -> sewn -> stuffed); the macro is the money shot (weave/seam in real
// light) and MUST become a real photo (a render reads as AI up close).
const ATELIER_BG = "radial-gradient(130% 100% at 20% 0%, #2f251d 0%, #241b14 55%, #1b130d 100%)";

function CraftStep({ step, index }: { step: CraftStepData; index: number }) {
  const { t } = useTranslation("landing");
  const { ref, revealed } = useReveal<HTMLLIElement>();
  return (
    <li
      ref={ref}
      data-revealed={revealed}
      style={{ transitionDelay: `${index * 90}ms` }}
      className={cn(REVEAL_TRANSITION, revealState(revealed))}
    >
      <figure>
        <div className="relative">
          <PhotoSlot
            image={step.image}
            alt={t(step.image.altKey)}
            briefId="craftSteps"
            tone="dark"
            aspectClassName="aspect-square"
            icon={Scissors}
          />
          <span
            aria-hidden="true"
            className="absolute bottom-3 left-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-primary font-display text-sm font-semibold text-primary-foreground shadow"
          >
            {index + 1}
          </span>
        </div>
        <figcaption>
          <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-white">
            {t(`craft.steps.${step.id}.title`)}
          </h3>
          <p className="mt-1.5 text-pretty text-sm leading-relaxed text-amber-50/70">
            {t(`craft.steps.${step.id}.body`)}
          </p>
        </figcaption>
      </figure>
    </li>
  );
}

export function HowItsMade() {
  const { t } = useTranslation("landing");
  const { steps, macro } = LANDING_CONTENT.craft;
  const sizes = Array.from(new Set(LANDING_CONTENT.trust.sizesCm.flat()))
    .sort((a, b) => a - b)
    .join(", ");

  const materials = [
    { icon: Shirt, text: t("craft.material.fabric") },
    { icon: Cloud, text: t("craft.material.fill") },
    { icon: Ruler, text: t("craft.material.size", { sizes }) },
  ];

  return (
    <section
      id="how"
      aria-labelledby="how-heading"
      className="relative isolate overflow-hidden py-[clamp(4.5rem,9vw,7.5rem)] text-white"
      style={{ background: ATELIER_BG }}
    >
      <div className="mx-auto w-full max-w-6xl px-6 sm:px-8">
        <Reveal>
          <div className="max-w-2xl">
            <h2
              id="how-heading"
              className="text-balance font-display text-[clamp(1.75rem,1.1rem+2.4vw,2.5rem)] font-semibold leading-[1.12] tracking-tight text-white"
            >
              {t("craft.heading")}
            </h2>
            <p className="mt-4 text-pretty text-[clamp(1.05rem,1rem+0.3vw,1.2rem)] leading-relaxed text-amber-50/75">
              {t("craft.lead")}
            </p>
          </div>
        </Reveal>

        <ol className="mt-12 grid gap-x-8 gap-y-12 sm:grid-cols-3">
          {steps.map((step, index) => (
            <CraftStep key={step.id} step={step} index={index} />
          ))}
        </ol>

        {/* The macro craft close-up: the strongest anti-"AI render" signal, given
            its own wide frame beside the materials story. */}
        <div className="mt-16 grid grid-cols-1 items-center gap-10 border-t border-white/10 pt-12 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
          <Reveal>
            <PhotoSlot
              image={macro}
              alt={t("craft.macroAlt")}
              briefId="craftMacro"
              tone="dark"
              aspectClassName="aspect-[3/2]"
              label={t("craft.macroPlaceholder")}
            />
          </Reveal>
          <Reveal delayMs={100}>
            <h3 className="font-display text-xl font-semibold tracking-tight text-white">
              {t("craft.material.heading")}
            </h3>
            <ul className="mt-5 space-y-4">
              {materials.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-start gap-3">
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  <span className="text-sm leading-relaxed text-amber-50/80">{text}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
